import { FC, PropsWithChildren, useEffect } from 'react';
import { MAIN_ROUTE } from 'shared/model';
import { useNavigate } from 'react-router';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { replicaStore } from 'entities/tavus/replica/model/replicaStore';
import {
  EPersonaIntegrationMode,
  EPersonaViewMode,
  ITavusPersona,
  tavusPersonaStore,
  timerConfigValidationSchema,
  ITavusPersonaFormik,
  EIntegrationSettingsType
} from 'entities/tavus/persona';
import { AvatarEditPersonaSidebar } from './AvatarEditPersonaSidebar';
import { AvatarIdentityForm } from 'widgets/avatar-identity-form';
import { TopBar } from './TopBar';
import { Preview } from './Preview';
import { toast } from 'react-toastify';

export const AvatarEditPersonaLayout: FC<
  PropsWithChildren<{ persona: ITavusPersona }>
> = ({ persona }) => {
  const { currentReplica, fetchReplicas } = replicaStore((state) => ({
    currentReplica: state.currentReplica,
    getReplicaById: state.getReplicaById,
    replicas: state.replicas,
    fetchReplicas: state.fetchReplicas
  }));
  const { fetchPersonas } = tavusPersonaStore((state) => ({
    fetchPersonas: state.fetchPersonas,
    tavusPersonasLoading: state.tavusPersonasLoading
  }));

  useEffect(() => {
    fetchReplicas();
    fetchPersonas();
  }, []);

  const { isPersonaCreating, updatePersona, sideBarOpened } = tavusPersonaStore(
    (state) => ({
      isPersonaCreating: state.isPersonaCreating,
      updatePersona: state.updatePersona,
      updateIntegrationSettings: state.updateIntegrationSettings,
      sideBarOpened: state.sideBarOpened
    })
  );

  const nav = useNavigate();

  const { embed, widget } = persona;

  const formik = useFormik<ITavusPersonaFormik>({
    initialValues: {
      isRecording: persona?.isRecording || false,
      personaIntegrationViewMode: EPersonaIntegrationMode.EMBED,
      personaViewMode: EPersonaViewMode.ON_CALL,
      name: persona.persona_name,
      defaultReplicaId: persona.default_replica_id,
      systemPrompt: persona.system_prompt,
      context: persona.context,
      customGreeting:
        persona.custom_greeting || 'Ready to Meet Your AI Assistant?',
      custom_background: persona.custom_background || '',
      model: persona.model || 'llama',
      timer_config:
        (() => {
          try {
            return persona?.timer_config
              ? JSON.parse(persona.timer_config as any)
              : '';
          } catch {
            return '';
          }
        })() ?? '',
      timer_config_enabled: persona?.timer_config_enabled ?? false,
      voiceId: persona.voice_id || '',
      preconversationConfig: persona.preconversation_config || {
        fields: []
      },
      defaultLanguage: persona?.default_language || 'english',
      perceptionModel: persona?.perception_model || 'off',
      ambientAwarenessQueries: persona?.ambient_awareness_queries || [],
      embed: {
        cta_primary_button_link: embed?.cta_primary_button_link ?? null,
        video_agent_name:
          embed?.video_agent_name ?? persona?.persona_name ?? 'Video agent',
        is_form_enabled: embed?.is_form_enabled ?? false,
        description: embed?.description ?? 'Ready to Meet Your AI Assistant?',
        button_text: embed?.button_text ?? 'Start call',
        second_button_text: embed?.second_button_text ?? 'Learn more',
        powered_by: embed?.powered_by ?? true,
        dark_mode: widget?.dark_mode ?? false,
        main_color: embed?.main_color ?? '#C34DFF',
        more_personalised_colors: embed?.more_personalised_colors ?? false,
        font: embed?.font ?? 'Inter',
        camera: embed?.camera ?? true,
        language_selection: embed?.language_selection ?? true,
        captions_by_default: embed?.captions_by_default ?? false,
        domains: embed?.domains ?? [],
        type: EIntegrationSettingsType.EMBED,
        api_key_id: embed?.api_key_id ?? '',
        screen_share: widget?.screen_share ?? false,
        cta_secondary_button_link: embed?.cta_secondary_button_link ?? '',
        cta_primary_button_text: embed?.cta_primary_button_text ?? 'Start call',
        cta_secondary_button_text:
          embed?.cta_secondary_button_text ?? 'Learn more',
        video_call_ended_custom_message:
          embed?.video_call_ended_custom_message ??
          'Sign up and use Yepic conversational video APIs free.'
      },
      widget: {
        cta_primary_button_link: widget?.cta_primary_button_link ?? null,
        bubble_background_color: widget?.bubble_background_color ?? '#C34DFF',
        bubble_icon_color: widget?.bubble_icon_color ?? '#fff',
        bubble_icon_custom: widget?.bubble_icon_custom ?? false,
        second_button_text: widget?.second_button_text ?? 'Learn more',
        bubble_icon_side: widget?.bubble_icon_side ?? 'right',
        bubble_icon_url: widget?.bubble_icon_url ?? '',
        bubble_icon_variant: widget?.bubble_icon_variant ?? 'first',
        custom_widget_trigger: widget?.custom_widget_trigger ?? '',
        welcome_button_color: widget?.welcome_button_color ?? '#C34DFF',
        dark_mode: widget?.dark_mode ?? false,
        video_agent_name:
          widget?.video_agent_name ?? persona?.persona_name ?? 'Video agent',
        is_form_enabled: widget?.is_form_enabled ?? false,
        description: widget?.description ?? 'Ready to Meet Your AI Assistant?',
        button_text: widget?.button_text ?? 'Start call',
        powered_by: widget?.powered_by ?? true,
        main_color: widget?.main_color ?? '#C34DFF',
        more_personalised_colors: widget?.more_personalised_colors ?? false,
        font: widget?.font ?? 'Inter',
        camera: widget?.camera ?? true,
        language_selection: widget?.language_selection ?? true,
        captions_by_default: widget?.captions_by_default ?? false,
        domains: widget?.domains ?? [],
        type: EIntegrationSettingsType.WIDGET,
        api_key_id: widget?.api_key_id ?? '',
        screen_share: widget?.screen_share ?? false,
        video_call_ended_custom_message:
          widget?.video_call_ended_custom_message ??
          'Sign up and use Yepic conversational video APIs free.',
        cta_secondary_button_link: widget?.cta_secondary_button_link ?? '',
        cta_primary_button_text:
          widget?.cta_primary_button_text ?? 'Start call',
        cta_secondary_button_text:
          widget?.cta_secondary_button_text ?? 'Learn more'
      }
    },
    validationSchema: Yup.object({
      name: Yup.string(),
      systemPrompt: Yup.string()
        .required('Please enter description prompt')
        .max(80, 'Please make description shorter'),
      customGreeting: Yup.string().max(500, 'Please make greeting shorter'),
      ambientAwarenessQueries: Yup.array().of(Yup.string()),
      timerConfig: timerConfigValidationSchema,
      preconversationConfig: Yup.object({
        fields: Yup.array().of(
          Yup.object().shape({
            name: Yup.string()
              .matches(/^[A-Za-zА-Яа-я_]+$/, 'No numbers or spaces allowed')
              .notOneOf(['language'], 'This name is reserved')
              .required('Required'),
            value: Yup.string().optional()
          })
        )
      })
    }),
    onSubmit: async (values) => {
      formik.setSubmitting(true);
      try {
        await updatePersona(persona.id, {
          persona_name: values.name || 'Video Agent Name',
          default_replica_id: currentReplica?.replica_id!,
          context: values.context,
          system_prompt: values.systemPrompt,
          custom_greeting: values.customGreeting,
          custom_background: values.custom_background as string | '',
          timer_config_enabled: values.timer_config_enabled,
          model: values.model,
          timer_config: JSON.stringify(
            values.timer_config || { duration: 0, events: [] }
          ),
          preconversation_config: values.preconversationConfig,
          voice_id: values.voiceId,
          default_language: values.defaultLanguage,
          perception_model: values.perceptionModel,
          ambient_awareness_queries: values.ambientAwarenessQueries.filter(
            (item) => item?.trim
          ),
          isRecording: values.isRecording,
          embed: {
            video_agent_name: values.embed.video_agent_name,
            is_form_enabled: values.embed.is_form_enabled,
            description: values.embed.description,
            button_text: values.embed.button_text,
            second_button_text: values.embed.second_button_text,
            powered_by: values.embed.powered_by,
            dark_mode: values.widget.dark_mode,
            main_color: values.embed.main_color,
            more_personalised_colors: values.embed.more_personalised_colors,
            font: values.embed.font,
            camera: values.embed.camera,
            language_selection: values.embed.language_selection,
            captions_by_default: values.embed.captions_by_default,
            domains: values.embed.domains,
            type: values.embed.type,
            api_key_id: values.embed.api_key_id,
            screen_share: values.embed.screen_share,
            cta_primary_button_text: values.embed.cta_primary_button_text,
            cta_secondary_button_text: values.embed.cta_secondary_button_text,
            cta_secondary_button_link: values.embed.cta_secondary_button_link,
            video_call_ended_custom_message:
              values.embed.video_call_ended_custom_message,
            cta_primary_button_link: values.embed.cta_primary_button_link
          },
          widget: {
            bubble_icon_color: values.widget.bubble_icon_color,
            bubble_icon_custom: values.widget.bubble_icon_custom,
            bubble_icon_side: values.widget.bubble_icon_side,
            bubble_icon_url: values.widget.bubble_icon_url,
            custom_widget_trigger: values.widget.custom_widget_trigger,
            welcome_button_color: values.widget.welcome_button_color,
            bubble_icon_variant: values.widget.bubble_icon_variant,
            bubble_background_color: values.widget.bubble_background_color,
            is_form_enabled: values.widget.is_form_enabled,
            video_agent_name: values.widget.video_agent_name,
            dark_mode: values.widget.dark_mode,
            description: values.widget.description,
            button_text: values.widget.button_text,
            second_button_text: values.widget.second_button_text,
            powered_by: values.widget.powered_by,
            main_color: values.widget.main_color,
            more_personalised_colors: values.widget.more_personalised_colors,
            font: values.widget.font,
            camera: values.widget.camera,
            language_selection: values.widget.language_selection,
            captions_by_default: values.widget.captions_by_default,
            domains: values.widget.domains,
            type: values.widget.type,
            api_key_id: values.widget.api_key_id,
            screen_share: values.widget.screen_share,
            cta_primary_button_text: values.widget.cta_primary_button_text,
            cta_secondary_button_text: values.widget.cta_secondary_button_text,
            cta_secondary_button_link: values.widget.cta_secondary_button_link,
            cta_primary_button_link: values.widget.cta_primary_button_link,
            video_call_ended_custom_message:
              values.widget.video_call_ended_custom_message
          }
        });

        toast('Changes saved successfully.');
        nav(MAIN_ROUTE);
      } catch (error) {
        console.error('update avatar error:', error);
      } finally {
        formik.setSubmitting(false);
      }
    }
  });

  useEffect(() => {
    if (currentReplica?.replica_id !== formik.values.defaultReplicaId) {
      formik.setFieldValue('defaultReplicaId', currentReplica?.replica_id);
    }
  }, [currentReplica]);

  const isDisabled = !formik.dirty || formik.isSubmitting;

  return (
    <form onSubmit={formik.handleSubmit}>
      <div
        className={`ag-bg-gray-100 ag-grid
      ag-grid-cols-1 ag-overflow-y-auto lg:ag-grid-rows-[0.5rem,calc(100vh-4rem)] lg:ag-grid-cols-[644px,calc(100vw-644px)] ag-h-screen ag-w-full`}
      >
        <div className="ag-col-span-full">
          <TopBar
            isDisabled={isDisabled}
            isPersonaCreating={isPersonaCreating}
          />
        </div>
        <AvatarEditPersonaSidebar
          IdentityFormComponent={AvatarIdentityForm}
          formik={formik}
          persona={persona}
        />

        <Preview currentReplica={currentReplica} formik={formik} />
      </div>
    </form>
  );
};
